import { useMemo } from 'react';
import { draftErrorFilterKeywords } from '@web-config-app/validation';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useConfigUserFeedback } from '../../state/config-user-feedback-state/config-user-feedback-state';

/**
 * Derives validation settings from tenant config action override to show entity form errors.
 * Returns:
 * - errorFilterKeywords: list of AJV error keywords to hide. Precedence:
 *   1) explicit override array (including [] meaning "show all errors"),
 *   2) tenant liveValidation.filterOutErrorsByKeywords,
 *   3) default draftErrorFilterKeywords.
 * - shouldShowFormValidation: true when tenant live validation is enabled or a validation session (e.g. publish) is active.
 */
export const useValidationSettings = () => {
  const { frontendValidation: tenantValidation } = useConfigApp();
  const { sessionActive, errorFilterKeywordsActionOverride } =
    useConfigUserFeedback((state) => ({
      sessionActive: state.showFormValidation,
      errorFilterKeywordsActionOverride:
        state.errorFilterKeywordsActionOverride,
    }));

  const { errorFilterKeywords } = useMemo(() => {
    if (Array.isArray(errorFilterKeywordsActionOverride)) {
      /** Highest precedence: an explicit override (e.g., from an operation like Publish).
       * Honor empty array meaning "show all errors".
       */
      return {
        errorFilterKeywords: errorFilterKeywordsActionOverride,
      };
    }

    /** Tenant-level configuration or defaults. */
    const configuredKeywords =
      tenantValidation?.liveValidation?.filterOutErrorsByKeywords ??
      draftErrorFilterKeywords;

    return {
      errorFilterKeywords: configuredKeywords,
    };
  }, [
    errorFilterKeywordsActionOverride,
    tenantValidation?.liveValidation?.filterOutErrorsByKeywords,
  ]);

  const liveValidationEnabled = tenantValidation?.liveValidation?.enabled;
  /** Show validation if live is on, or if the user started a validation session (e.g. publish) is active. */
  const shouldShowFormValidation = liveValidationEnabled || sessionActive;

  return {
    errorFilterKeywords,
    shouldShowFormValidation,
  };
};
